/**
 * @file `AdminTerminal` — read-only terminal observer driver. Wraps one
 *       `AdminTerminalSub` subscription and exposes the minimal driver
 *       surface `<AdminTerminalView/>` consumes.
 * @module @labcraft/admin/src/client/terminal
 *
 * The admin console observes a running Machine's pty; it never types.
 * So the driver is receive-only:
 *   - `onData` — pty stdout bytes (server → browser), written to xterm.
 *   - `onResize` — the learner's current winsize, mirrored so the admin
 *     terminal (and the drawer around it) matches the learner's
 *     dimensions live.
 *   - `onStatus` — connection status, so the view can react to open.
 *
 * There is deliberately no `write` / `resize` — the operator drives
 * nothing. Constructed per-attach by the `Hypervisor` namespacer's
 * `terminal(id)`; `close()` tears the subscription down on unmount.
 */

import type { TypedSubscription, Unsubscribe } from '@labcraft/core/frontend'
import type { WsTransport } from './index.ts'
import {
  AdminTerminalSub,
  type AdminTerminalClientFrame,
  type AdminTerminalServerFrame,
} from '../shared/terminal_subscription.ts'

/** The concrete `TypedSubscription` this driver wraps. */
type TypedSub = TypedSubscription<
  AdminTerminalClientFrame,
  AdminTerminalServerFrame
>

/** Handler for pty stdout bytes. */
type DataHandler = (bytes: Uint8Array) => void

/** Handler for the mirrored learner winsize (cols, rows). */
type ResizeHandler = (cols: number, rows: number) => void

/** Handler for connection-status changes. */
type StatusHandler = (status: string) => void

/**
 * Read-only observer over one Machine's pty. Satisfies the
 * `<AdminTerminalView/>` driver shape (`onData` / `onResize` /
 * `onStatus`) without a `write` surface — the operator observes only.
 */
export class AdminTerminal {
  private readonly ws: WsTransport
  private readonly id: string
  private sub: TypedSub | null = null
  private readonly subUnsubs: Unsubscribe[] = []
  private readonly dataHandlers = new Set<DataHandler>()
  private readonly resizeHandlers = new Set<ResizeHandler>()
  private readonly statusHandlers = new Set<StatusHandler>()

  constructor(ws: WsTransport, id: string) {
    this.ws = ws
    this.id = id
  }

  /**
   * Open the subscription to `/api/hypervisor/:id/terminal` and wire its
   * channels into the local handler sets. Binary → `onData`; the
   * `resize` server frame → `onResize`; status → `onStatus`. Lifecycle
   * frames are accepted by the contract but ignored here (the panel
   * reads state from the machine list).
   */
  attach(): void {
    const sub = this.ws.subscribe(AdminTerminalSub, {
      params: { id: this.id },
    })
    this.sub = sub
    this.subUnsubs.push(
      sub.onBinary(bytes => {
        for (const h of this.dataHandlers) h(bytes)
      }),
      sub.onData(frame => {
        if (frame.type === 'resize') {
          for (const h of this.resizeHandlers) {
            h(frame.payload.cols, frame.payload.rows)
          }
        }
      }),
      sub.onStatus(status => {
        for (const h of this.statusHandlers) h(status)
      }),
    )
  }

  /** Subscribe to pty stdout bytes. */
  onData(handler: DataHandler): Unsubscribe {
    this.dataHandlers.add(handler)
    return () => this.dataHandlers.delete(handler)
  }

  /** Subscribe to the mirrored learner winsize. */
  onResize(handler: ResizeHandler): Unsubscribe {
    this.resizeHandlers.add(handler)
    return () => this.resizeHandlers.delete(handler)
  }

  /** Subscribe to connection-status changes. */
  onStatus(handler: StatusHandler): Unsubscribe {
    this.statusHandlers.add(handler)
    return () => this.statusHandlers.delete(handler)
  }

  /** Tear down the subscription and drop every handler. */
  close(): void {
    for (const off of this.subUnsubs) off()
    this.subUnsubs.length = 0
    this.dataHandlers.clear()
    this.resizeHandlers.clear()
    this.statusHandlers.clear()
    this.sub?.unsubscribe()
    this.sub = null
  }
}
